import { useEffect, useRef, useState } from 'react';
import type { PaDoc, ToSendItem } from '../types';
import { money } from '../utils';
import { WAITING_DOC_META } from '../store';
import { Check, SourceRow, Spinner } from './common';
import { PMS } from '@/features/shared/pms';

interface Props {
  item: ToSendItem;
  sending: boolean;
  onSend: () => void;
  onChoose: (optionId: string) => void;
  onDontSend: () => void;
  onBack?: () => void;
}

export function Detail(props: Props) {
  const { item } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [viewDoc, setViewDoc] = useState<PaDoc | null>(null);
  const [addOpen, setAddOpen] = useState<null | { name: string }>(null);
  const [extraDocs, setExtraDocs] = useState<PaDoc[]>([]);
  const [attachedIds, setAttachedIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    headingRef.current?.focus();
    setChosen(null);
    setViewDoc(null);
    setAddOpen(null);
    setExtraDocs([]);
    setAttachedIds(new Set());
  }, [item.id]);

  const allDocs = [...item.documents, ...extraDocs];
  const docStatus = (d: PaDoc): PaDoc['status'] => (attachedIds.has(d.id) ? 'ok' : d.status);
  const reqDocs = allDocs.filter((d) => d.required);
  const attached = reqDocs.filter((d) => docStatus(d) === 'ok').length;
  const needsYou = item.group === 'needs_you';
  // Oct 22: "take an X-ray, then send" sends nothing; the item waits in To send until the X-ray arrives.
  const waiting = item.waitingDoc === true;

  const attachDoc = (name: string) => {
    const existing = allDocs.find((d) => d.name === name && docStatus(d) !== 'ok');
    if (existing) {
      setAttachedIds((s) => new Set(s).add(existing.id));
    } else {
      const id = `extra-${String(extraDocs.length + 1)}`;
      setExtraDocs((list) => [...list, { id, name, meta: 'Added just now · from your computer', status: 'ok', required: false }]);
    }
    setAddOpen(null);
  };

  const onOption = (optionId: string) => {
    const opt = item.decision?.options.find((o) => o.id === optionId);
    if (!opt) return;
    if (opt.effect === 'dont_send') {
      props.onDontSend();
      return;
    }
    setChosen(optionId);
  };

  const sendDisabled = props.sending || waiting || (needsYou && !chosen);
  const onSendClick = () => {
    if (chosen) props.onChoose(chosen);
    else props.onSend();
  };

  return (
    <main className="pa-detail">
      {props.onBack ? (
        <button type="button" className="pa-back" onClick={props.onBack}>
          ← All pre-auths
        </button>
      ) : null}
      <div className="pa-detail-inner">
        {/* header */}
        <section className="pa-claimhead" aria-labelledby="pa-name">
          <div>
            <h2 id="pa-name" className="pa-patient" tabIndex={-1} ref={headingRef}>
              {item.patient}
            </h2>
            <p className="pa-chsub">
              {item.payer.name} · member {item.memberId} · {item.provider}
            </p>
            <p className="pa-chmeta">
              {item.procedureWords} · appointment {item.appointmentDisplay} · found when the appointment was booked on{' '}
              {item.bookedDisplay}
            </p>
          </div>
          <div className="pa-right">
            <div className="pa-meta">Treatment</div>
            <div className="pa-bigfig num">{money(item.treatment)}</div>
          </div>
        </section>

        {/* why */}
        <section className="pa-card" aria-labelledby="pa-why">
          <h3 className="pa-h3" id="pa-why">
            {item.why.headline}
          </h3>
          <p className="pa-consequence">{item.why.consequence}</p>
          {item.why.timing ? (
            <div className="pa-timing">
              <span className="pa-tick" aria-hidden="true">
                <Check />
              </span>
              <div>
                <span style={{ fontWeight: 600 }}>There’s time.</span> {item.why.timing.sentence}
              </div>
            </div>
          ) : null}
          <SourceRow sources={item.why.sources} />
        </section>

        {/* decision (needs you) */}
        {waiting ? (
          <section className="pa-card" aria-labelledby="pa-wait-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className="pa-badge b-mute">Waiting for the X-ray</span>
              <h3 className="pa-h3" id="pa-wait-title">Not sent yet</h3>
            </div>
            <p className="pa-consequence" role="status">{WAITING_DOC_META}</p>
          </section>
        ) : null}

        {needsYou && !waiting && item.decision ? (
          <section className="pa-decision" aria-labelledby="pa-dec-title">
            <div className="pa-decision-head">
              <span className="pa-badge b-white" style={{ background: '#fff', border: '1px solid #e9c98a', color: 'var(--warning)' }}>
                Needs your decision
              </span>
              <h3 className="pa-decision-title" id="pa-dec-title">
                {item.decision.title}
              </h3>
            </div>
            <div className="pa-decision-body">
              <p style={{ margin: 0, color: 'var(--fg2)' }}>{item.decision.body}</p>
              <div className="pa-options" role="radiogroup" aria-labelledby="pa-dec-title">
                {item.decision.options.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={chosen === o.id}
                    className={`pa-option${o.style === 'ghost' ? ' pa-option-ghost' : ''}`}
                    style={chosen === o.id ? { background: 'var(--psoft)', borderColor: 'var(--primary)', fontWeight: 600 } : undefined}
                    onClick={() => { onOption(o.id); }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <p className="pa-meta" style={{ margin: 0 }}>{item.decision.meta}</p>
            </div>
          </section>
        ) : null}

        {/* procedures */}
        <section className="pa-card" aria-labelledby="pa-proc">
          <h3 className="pa-h3" id="pa-proc" style={{ marginBottom: 12 }}>
            Procedures
          </h3>
          <div className="pa-tbl-wrap">
            <table className="pa-tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Tooth</th>
                  <th className="ta-right">Fee</th>
                </tr>
              </thead>
              <tbody>
                {item.procedures.map((p) => (
                  <tr key={`${p.code}-${p.tooth ?? ''}`}>
                    <td className="pa-code">{p.code}</td>
                    <td>{p.description}</td>
                    <td>{p.tooth ?? '—'}</td>
                    <td className="ta-right num">{money(p.fee)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pa-tbl-foot">Change it on the appointment; ELVA re-checks.</div>
        </section>

        {/* documents */}
        <section className="pa-card" aria-labelledby="pa-docs">
          <h3 className="pa-h3" id="pa-docs">
            Documents {item.payer.short} asks for · {attached} of {reqDocs.length} attached
          </h3>
          {attached === reqDocs.length ? <p className="pa-meta" style={{ marginTop: 4 }}>ELVA found each one in {PMS}.</p> : null}
          {allDocs.map((d) => {
            const st = docStatus(d);
            const needsAdd = st === 'helpful' || st === 'missing' || st === 'too_old';
            return (
              <div key={d.id} className="pa-doc">
                {st === 'ok' ? (
                  <span className="pa-tick" aria-hidden="true"><Check /></span>
                ) : st === 'helpful' ? (
                  <span className="pa-badge b-mute">Helpful</span>
                ) : (
                  <span className="pa-badge b-warn">{st === 'too_old' ? 'Too old' : 'Missing'}</span>
                )}
                <div className="pa-doc-main">
                  <div className="pa-doc-name">{d.name}</div>
                  <div className="pa-doc-meta">{d.meta}</div>
                </div>
                <button
                  type="button"
                  className="pa-btn pa-btn-ghost pa-btn-sm"
                  onClick={() => { if (needsAdd) setAddOpen({ name: d.name }); else setViewDoc(d); }}
                >
                  {needsAdd ? 'Add' : 'View'}
                </button>
              </div>
            );
          })}
          <div style={{ marginTop: 10 }}>
            <button type="button" className="pa-btn pa-btn-ghost pa-btn-sm" onClick={() => { setAddOpen({ name: '' }); }}>Add a document</button>
          </div>
        </section>

        {/* narrative */}
        <NarrativeCard item={item} />

        {/* action bar */}
        <div className="pa-actionbar">
          <button type="button" className="pa-btn pa-btn-primary pa-btn-lg" disabled={sendDisabled} onClick={onSendClick}>
            {props.sending ? <Spinner label="Sending…" /> : `Send to ${item.payer.short}`}
          </button>
          <button type="button" className="pa-btn pa-btn-ghost" onClick={props.onDontSend}>
            Don’t send
          </button>
          <span className="pa-actionbar-meta">Sent electronically, with the documents. ELVA follows {item.payer.short}’s answer.</span>
        </div>

        {/* history */}
        <p className="pa-history">History: {item.history.join(' · ')}</p>
      </div>
      {viewDoc ? <DocViewerModal doc={viewDoc} onClose={() => { setViewDoc(null); }} /> : null}
      {addOpen ? <AddDocModal name={addOpen.name} onCancel={() => { setAddOpen(null); }} onAttach={attachDoc} /> : null}
    </main>
  );
}

function PaModal({ label, onClose, children }: { label: string; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button, input')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="pa-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="pa-dialog" role="dialog" aria-modal="true" aria-label={label}>{children}</div>
    </div>
  );
}

/** Read-only mock preview of an attached document (no real file is stored). */
function DocViewerModal({ doc, onClose }: { doc: PaDoc; onClose: () => void }) {
  return (
    <PaModal label={`View ${doc.name}`} onClose={onClose}>
      <h2 className="pa-dialog-title">{doc.name}</h2>
      <p className="pa-meta" style={{ margin: 0 }}>{doc.meta}</p>
      <div
        role="img"
        aria-label={`Preview of ${doc.name}`}
        style={{ marginTop: 12, height: 220, borderRadius: 8, border: '1px solid var(--border)', background: 'repeating-linear-gradient(135deg, var(--psoft) 0 14px, #fff 14px 28px)', display: 'grid', placeItems: 'center', color: 'var(--fg2)', fontSize: 13 }}
      >
        Document preview (mock)
      </div>
      <div className="pa-dialog-actions">
        <button type="button" className="pa-btn pa-btn-primary" onClick={onClose}>Close</button>
      </div>
    </PaModal>
  );
}

/** Mock "attach a document" flow — no real upload; it marks the doc attached. */
function AddDocModal({ name, onCancel, onAttach }: { name: string; onCancel: () => void; onAttach: (name: string) => void }) {
  const [fileName, setFileName] = useState('');
  const [typed, setTyped] = useState(name);
  const finalName = name || (typed.trim() || 'Added document');
  return (
    <PaModal label="Add a document" onClose={onCancel}>
      <h2 className="pa-dialog-title">{name ? `Add “${name}”` : 'Add a document'}</h2>
      {name ? null : (
        <input className="pa-field" placeholder="What is this document?" value={typed} onChange={(e) => { setTyped(e.target.value); }} />
      )}
      <label className="pa-drop" style={{ display: 'block', cursor: 'pointer', marginTop: 10, padding: '18px', border: '1px dashed var(--border)', borderRadius: 8, textAlign: 'center', color: 'var(--fg2)', fontSize: 13 }}>
        {fileName ? `${fileName} ready` : 'Drop a file here, or choose one'}
        <input type="file" accept="image/*,.pdf,.tiff" className="pa-vh" onChange={(e) => { setFileName(e.target.files?.[0]?.name ?? ''); }} />
      </label>
      <p className="pa-meta" style={{ margin: '8px 0 0' }}>ELVA attaches it to the pre-auth. Nothing is sent to the payer until you send it.</p>
      <div className="pa-dialog-actions">
        <button type="button" className="pa-btn pa-btn-primary" disabled={fileName === ''} onClick={() => { onAttach(finalName); }}>Attach</button>
        <button type="button" className="pa-btn" onClick={onCancel}>Cancel</button>
      </div>
    </PaModal>
  );
}

function NarrativeCard({ item }: { item: ToSendItem }) {
  const [text, setText] = useState(item.narrative.text);
  const [rewriting, setRewriting] = useState(false);
  const [rewritten, setRewritten] = useState(false);
  useEffect(() => {
    setText(item.narrative.text);
    setRewritten(false);
  }, [item.id, item.narrative.text]);

  const rewrite = async () => {
    setRewriting(true);
    await new Promise((r) => setTimeout(r, 800));
    setText(item.narrative.text);
    setRewritten(true);
    setRewriting(false);
  };

  return (
    <section className="pa-card" aria-labelledby="pa-narr">
      <div className="pa-card-head">
        <h3 className="pa-h3" id="pa-narr">Narrative</h3>
        <button type="button" className="pa-btn pa-btn-ghost pa-btn-sm" disabled={rewriting} onClick={() => { void rewrite(); }}>
          {rewriting ? <Spinner label="Rewriting…" /> : 'Rewrite'}
        </button>
      </div>
      <label className="pa-vh" htmlFor="pa-narr-text">Clinical narrative</label>
      <textarea id="pa-narr-text" className="pa-textarea" value={text} onChange={(e) => { setText(e.target.value); }} />
      <div className="pa-origin">
        {rewritten ? 'Rewritten by ELVA just now.' : `Written by ELVA from ${item.narrative.sources}.`}
      </div>
    </section>
  );
}
